/**
 * One comparison: two sides, their documents, and the diff.
 *
 * ⚠️ IT IS A NARROWER DOOR ONTO AN INSPECTOR ACCOUNT, NOT A SEPARATE THING.
 * Same account, same sides, same extraction, same server-computed diff — and
 * the link at the bottom opens the very same record in the Account Inspector
 * with everything else it offers. What this page removes is the rest of the
 * Inspector: status stages, coverage gaps, the workbook and the client
 * presentation. Somebody who came to compare two policies should not have to
 * scroll past a generate-a-presentation button to do it.
 *
 * ⚠️ TWO FIXED SIDES, NOT A GENERAL SIDE MANAGER. The Inspector allows up to
 * four and lets you name them; this shows the two it was started with. A
 * comparison tool that opens with "add a side" is a tool that has not decided
 * what it is for.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { PolicyComparison } from '../components/PolicyComparison';
import { ApiError, api, type InspectorAccount, type InspectorDoc } from '../lib/api';

/** Base64 without the data: prefix, which is what the upload endpoint wants. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });

interface Side {
  id: string;
  label: string;
  isPrimary: boolean;
  documents: InspectorDoc[];
  extractedAt?: number;
}

export function CompareAccount(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState<InspectorAccount | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(
    () =>
      api
        .inspectorAccount(id)
        .then((r) => {
          setAccount(r.account);
          setErr('');
        })
        .catch((e: unknown) =>
          setErr(e instanceof ApiError ? e.message : 'Could not load that comparison.'),
        ),
    [id],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const docs: InspectorDoc[] = useMemo(() => account?.data?.documents ?? [], [account]);

  /*
    Resolved the same way the server resolves them — an account with no declared
    sides is one side holding everything, and documents on no side fall to the
    primary. See apps/api/src/inspector/sides.ts.
  */
  const sides: Side[] = useMemo(() => {
    const declared = account?.data?.sides ?? [];
    if (!declared.length) {
      return [{ id: 'primary', label: 'This account', isPrimary: true, documents: docs }];
    }
    const claimed = new Set(declared.flatMap((s) => s.documentIds ?? []));
    const orphans = docs.filter((d) => !claimed.has(d.id));
    const primaryIndex = Math.max(0, declared.findIndex((s) => s.id === 'primary'));
    return declared.map((s, i) => {
      const own = (s.documentIds ?? [])
        .map((docId) => docs.find((d) => d.id === docId))
        .filter((d): d is InspectorDoc => Boolean(d));
      return {
        id: s.id,
        label: s.label,
        isPrimary: i === primaryIndex,
        documents: i === primaryIndex ? [...own, ...orphans] : own,
        extractedAt: s.extractedAt,
      };
    });
  }, [account, docs]);

  const readCount = sides.filter(
    (s) => s.extractedAt || (s.isPrimary && account?.data?.extraction),
  ).length;

  /** Upload onto a named side: store the file, then claim it for that side. */
  const upload = async (sideId: string, files: FileList | null) => {
    if (!files?.length) return;
    setBusy(`up:${sideId}`);
    setErr('');
    try {
      for (const file of Array.from(files)) {
        const out = await api.inspectorUploadDoc(id, {
          kind: 'policy',
          fileName: file.name,
          mime: file.type || 'application/octet-stream',
          contentBase64: await toBase64(file),
        });
        await api.inspectorMoveDoc(id, out.document.id, sideId);
      }
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not upload that.');
    } finally {
      setBusy('');
    }
  };

  const read = async (sideId: string) => {
    setBusy(`read:${sideId}`);
    setErr('');
    try {
      await api.inspectorExtract(id, sideId);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not read those documents.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="wrap">
      <PageHero
        title={account?.named_insured || 'Comparison'}
        sub={account ? `${account.id} · ${sides.map((s) => s.label).join(' against ')}` : ''}
        action={
          <button className="btn btn-sm" type="button" onClick={() => navigate('/hub/compare')}>
            All comparisons
          </button>
        }
      />

      {err && <div className="empty">{err}</div>}

      <div className="cmp-sides">
        {sides.map((s) => {
          const done = Boolean(s.extractedAt || (s.isPrimary && account?.data?.extraction));
          return (
            <section className="panel cmp-side" key={s.id}>
              <header>
                <h3>{s.label}</h3>
                <span className={`tag ${done ? 'is-online' : 'is-draft'}`}>
                  {done ? 'Read' : s.documents.length ? 'Not read yet' : 'No documents'}
                </span>
              </header>

              {s.documents.length > 0 && (
                <ul className="cmp-files">
                  {s.documents.map((d) => (
                    <li key={d.id}>{d.fileName}</li>
                  ))}
                </ul>
              )}

              <label className="cmp-drop">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => void upload(s.id, e.target.files)}
                  disabled={busy.startsWith('up')}
                />
                <span>{busy === `up:${s.id}` ? 'Uploading…' : 'Add policy documents'}</span>
              </label>

              <button
                className="btn btn-sm btn-primary"
                type="button"
                disabled={!s.documents.length || busy.startsWith('read')}
                onClick={() => void read(s.id)}
              >
                {busy === `read:${s.id}` ? 'Reading…' : done ? 'Read again' : 'Read these'}
              </button>
            </section>
          );
        })}
      </div>

      {/*
        The identical component the Inspector renders. It shows nothing until
        two sides have been read, which is why the panels above say plainly
        where each side has got to.
      */}
      <PolicyComparison accountId={id} ready={readCount >= 2} />

      {readCount < 2 && (
        <p className="supp-note">
          The comparison appears once both sides have been read. Reading is the slow part — a
          minute or two per side, depending on how many pages the policy runs to.
        </p>
      )}

      <section className="panel lr-about">
        <div>
          <h3>This is an Account Inspector account</h3>
          <p>
            Same record, narrower door. Open it in the Inspector for the exposure workbook, the
            coverage gap review and the client presentation — none of which this page shows,
            because none of them are comparing anything.
          </p>
          <button
            className="btn btn-sm"
            type="button"
            style={{ marginTop: 12 }}
            onClick={() => navigate(`/hub/inspector/${id}`)}
          >
            Open in Account Inspector
          </button>
        </div>
      </section>
    </div>
  );
}
